import { Button, ButtonProps, Component, Panel } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { ExamplesPanel } from "./ExamplesPanel";

/**
 * Defines the button that allows examples to be expanded/collapsed.  When rendered as part of a heading row, it
 * expands/collapses all of the examples in that section (this is when *allButton* is true).
 */
export class ExpandButton extends Button {
    private isExpanded = false;
    private isAllButton: boolean;
    private enclosingPanel: Panel;

    constructor(enclosingPanel: Panel, isAllButton: boolean, props?: Partial<ButtonProps>) {
        super({
            imageName: ImageName.arrow,
            fontSize: "small",
            width: 180,
            borderWidth: 0,
            ...props
        });
        this.addClickListener(() => this.fireButtons(!this.isExpanded));
        this.isAllButton = isAllButton;
        this.caption = this.expandCaption;
        this.enclosingPanel = enclosingPanel;
    }

    private fireButtons(value: boolean) {
        this.findExpandButtons().forEach(button => button.setExpanded(value));
    }

    /**
     * This method finds ExpandButtons within the enclosing panel.
     * - If the enclosing panel is the section panel, we got here by the user clicking expand/collapse all.  In this
     *   case we want to find all of the ExpandButtons in that section except the expand/collapse all button.
     * - If the enclosing panel is a properly line panel, we got here by the user clicking the expand/collapse button in
     *   that property line panel.  The only button we will find is the button they clicked.
     */
    private findExpandButtons(): ExpandButton[] {
        return this.enclosingPanel.getRecursiveChildren().filter((component: Component) => {
            return component instanceof ExpandButton;
        }) as ExpandButton[];
    }

    private setExpanded(value: boolean) {
        if (this.isExpanded === value)
            return;
        this.isExpanded = value;
        if (this.isAllButton) {
            this.updateCaption();
            this.updateImageRotation();
        }
        else {
            this.enclosingPanel.getRecursiveChildren()
                .filter((component: Component) => component instanceof ExamplesPanel)
                .forEach(async (examplePanel: ExamplesPanel) => {
                this.updateCaption();
                this.updateImageRotation();
                value ? await examplePanel.showExamples() : await examplePanel.hideExamples(!this.isAllButton);
            });
        }
    }

    private updateCaption() {
        this.caption = this.isExpanded ? this.collapseCaption : this.expandCaption;
    }
    
    private get expandCaption(): string {
        return this.isAllButton ? "Expand All Examples" : "Examples";
    }

    private get collapseCaption(): string {
        return this.isAllButton ? "Collapse All Examples" : "Collapse";
    }

    private updateImageRotation() {
        this.imageRotation = this.isExpanded ? 180 : 0;
    }
}
